import {
  Map,
  TileLayer,
  Marker,
  Polyline,
  GeoJSON,
  Popup,
} from "react-leaflet";
import L from "leaflet";
import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { SERVER_URL } from "../config/config";
import indiaSOI from "../assets/india-soi.json";

const indiaCenter = [22.9734, 78.6569];

const TripTracking = () => {
  const { id } = useParams();
  const queryParams = new URLSearchParams(useLocation().search);
  const vehicleNo = queryParams.get("vehicle_no");
  const driverName = queryParams.get("driver_name");

  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [truckPosition, setTruckPosition] = useState(null);
  const [isReached, setIsReached] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [fromCoords, setFromCoords] = useState([21.1702, 72.8311]);
  const [toCoords, setToCoords] = useState([22.936, 72.5913]);

  const fetchCoordinates = async (cityName) => {
    const city = cityName.toLowerCase();

    // Handle known mismatches manually
    const hardcodedCoords = {
      dasrath: [22.2764, 73.1661],
      // Add more cities here if needed
    };

    if (hardcodedCoords[city]) {
      return hardcodedCoords[city];
    }

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?city=${cityName}&country=India&format=json`
    );
    const data = await response.json();
    if (data && data.length > 0) {
      const { lat, lon } = data[0];
      return [parseFloat(lat), parseFloat(lon)];
    }
    return null;
  };

  useEffect(() => {
    const fetchTrip = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${SERVER_URL}/trip/trip/${id}/estimate-arrival`
        );
        const data = await response.json();

        const fromCity = data.from_station?.split(",")[0]?.trim();
        const toCity = data.to_station?.split(",")[0]?.trim();

        const [fromCoord, toCoord] = await Promise.all([
          fetchCoordinates(fromCity),
          fetchCoordinates(toCity),
        ]);

        if (fromCoord) setFromCoords(fromCoord);
        if (toCoord) setToCoords(toCoord);

        setTrip(data);
      } catch (error) {
        console.error("Failed to fetch trip:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrip();
  }, [id]);

  useEffect(() => {
    if (!trip) return;

    const updateTruckPosition = () => {
      const hasReached = trip.status
        ?.toLowerCase()
        .includes("vehicle has reached");

      if (hasReached) {
        setIsReached(true);
        setProgressPercent(100);
        setTruckPosition(toCoords);
        return;
      }

      const start = new Date(trip.created_date);
      const now = new Date();
      const totalMinutes = trip.estimated_hours * 60;

      const minutesPassed = Math.max(
        0,
        Math.floor((now - start) / (1000 * 60))
      );
      const progress = Math.min(minutesPassed / totalMinutes, 1);

      const lat = fromCoords[0] + (toCoords[0] - fromCoords[0]) * progress;
      const lng = fromCoords[1] + (toCoords[1] - fromCoords[1]) * progress;

      setTruckPosition([lat, lng]);
      setIsReached(progress >= 1);
      setProgressPercent(Math.round(progress * 100));
    };

    updateTruckPosition();
    const interval = setInterval(updateTruckPosition, 30000);
    return () => clearInterval(interval);
  }, [trip, fromCoords, toCoords]);

  const cleanStationName = (station) => {
    if (!station) return "";
    const parts = station.split(",").map((p) => p.trim());
    return parts.length === 1
      ? parts[0]
      : `${parts[0]}, ${parts[parts.length - 1]}`;
  };

  const getTooltipContent = () => {
    const fromLocation = trip?.from_station || "";
    const toLocation = trip?.to_station || "";

    const etaTime = trip?.estimated_arrival_time
      ? new Date(trip.estimated_arrival_time).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

    return isReached ? (
      <div style={{ textAlign: "center" }}>
        <strong>🎯 Destination Reached!</strong>
        <br />
        <span style={{ color: "#b0bec5" }}>
          From: {cleanStationName(fromLocation)}
        </span>
        <br />
        <span style={{ color: "#b0bec5" }}>
          To: {cleanStationName(toLocation)}
        </span>
        <br />
        <span style={{ color: "#28a745", fontWeight: "bold" }}>✅ Arrived</span>
      </div>
    ) : (
      <div style={{ textAlign: "center" }}>
        <strong>🚚 Vehicle in Transit</strong>
        <br />
        <span style={{ color: "#b0bec5" }}>
          From: {cleanStationName(fromLocation)}
        </span>
        <br />
        <span style={{ color: "#b0bec5" }}>
          To: {cleanStationName(toLocation)}
        </span>
        <br />
        {etaTime && (
          <span style={{ color: "#007bff", fontWeight: "bold" }}>
            📅 ETA: {etaTime} ({trip.estimated_hours} hrs)
          </span>
        )}
        <br />
        <span style={{ color: "#ffc107", fontSize: "13px" }}>
          📏 Distance Covered: {progressPercent}%
        </span>
      </div>
    );
  };

  const createTruckMarker = () => {
    const truckIcon = L.divIcon({
      html: `<div style="
              width: 35px;
              height: 35px;
              background-image: url('https://cdn-icons-png.flaticon.com/512/535/535137.png');
              background-size: cover;
              background-repeat: no-repeat;
              cursor: pointer;
            "></div>`,
      iconSize: [35, 35],
      iconAnchor: [17, 35],
      className: "truck-marker",
    });

    return (
      truckPosition && (
        <Marker position={truckPosition} icon={truckIcon}>
          <Popup>{getTooltipContent()}</Popup>
        </Marker>
      )
    );
  };

  if (loading || !trip || !truckPosition) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          background: "#ffffff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          fontFamily: "Segoe UI, sans-serif",
          fontSize: "18px",
          color: "#5b2c6f",
        }}
      >
        Loading Trip...
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          fontSize: "24px",
          fontWeight: 600,
          padding: "12px 20px",
          backgroundColor: "#f1f0ff",
          borderBottom: "2px solid #b388ff",
          color: "#5b2c6f",
          fontFamily: "Segoe UI, sans-serif",
          letterSpacing: "0.5px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
          borderRadius: "8px 8px 0 0",
          display: "flex",
          justifyContent: "center",
        }}
      >
        Trip Tracking
      </div>

      <div
        style={{
          display: "flex",
          height: "100vh",
          width: "100%",
          background: "#f7f7f7",
        }}
      >
        <div
          style={{
            width: "40%",
            height: "30%",
            border: "1.5px solid #b388ff",
            backgroundColor: "#f9f2ff",
            borderRadius: "10px",
            padding: "15px",
            margin: "10px",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
            fontFamily: "Segoe UI, sans-serif",
            fontSize: "14px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "8px",
            }}
          >
            <strong style={{ color: "#333" }}>Trip Id: {trip.trip_id}</strong>
            <span style={{ color: "#6c757d" }}>
              {vehicleNo || "Vehicle No"}
            </span>
          </div>
          <p style={{ margin: "4px 0", display: "flex", alignItems: "center" }}>
            <strong style={{ marginRight: "6px", color: "#333" }}>
              Station From:
            </strong>
            <img
              src="https://cdn-icons-png.flaticon.com/512/149/149059.png"
              alt="from"
              style={{ width: "14px", height: "14px", marginRight: "6px" }}
            />
            <span style={{ color: "#2e7d32", fontWeight: 500 }}>
              {trip.from_station}
            </span>
          </p>
          <p style={{ margin: "4px 0", display: "flex", alignItems: "center" }}>
            <strong style={{ marginRight: "6px", color: "#333" }}>
              Station To:
            </strong>
            <img
              src="https://cdn-icons-png.flaticon.com/512/535/535137.png"
              alt="to"
              style={{ width: "14px", height: "14px", marginRight: "6px" }}
            />
            <span style={{ color: "#c62828", fontWeight: 500 }}>
              {trip.to_station}
            </span>
          </p>
          <p style={{ margin: "4px 0", display: "flex", alignItems: "center" }}>
            <strong style={{ marginRight: "6px", color: "#333" }}>
              Departure:
            </strong>
            <img
              src="https://cdn-icons-png.flaticon.com/512/1827/1827343.png"
              alt="calendar"
              style={{ width: "14px", height: "14px", marginRight: "6px" }}
            />
            <span style={{ color: "#1976d2" }}>
              {new Date(trip.created_date).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </p>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "#333" }}>
              <strong>Distance:</strong> {trip.distance_km?.toFixed(2)} km
            </span>
            <span style={{ color: "#666" }}>{driverName || "Driver Name"}</span>
          </div>
          <p
            style={{
              margin: "6px 0",
              marginTop: "12px",
              fontWeight: "bold",
              color: isReached ? "#28a745" : "#1976d2",
            }}
          >
            {isReached
              ? "✅ Destination Reached"
              : `ETA: ${
                  trip.estimated_arrival_time
                    ? `${new Date(trip.estimated_arrival_time).toLocaleString(
                        "en-IN",
                        {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        }
                      )} (${trip.estimated_hours} hrs)`
                    : "N/A"
                }`}
          </p>
        </div>

        <div
          style={{
            width: "60%",
            margin: "10px",
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          }}
        >
          <Map
            center={indiaCenter}
            zoom={6}
            scrollWheelZoom={true}
            style={{ height: "100%", width: "100%" }}
          >
            <GeoJSON
              data={indiaSOI}
              style={{ color: "#007b00", weight: 1.5, fillOpacity: 0.05 }}
            />
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />
            <Polyline positions={[fromCoords, toCoords]} color="purple" />
            {createTruckMarker()}
          </Map>
        </div>
      </div>
    </>
  );
};

export default TripTracking;
